import styles from "./cell.module.scss";
import { HTMLAttributes, PropsWithChildren, ReactNode, useEffect } from "react";
import { Skeleton } from "../../skeleton";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import classNames from "classnames";
import { useTable } from "../table-context";
import { useColumn } from "../column-context";
import { CellAction } from "./cell-action";
import { CellBoolean } from "./cell-boolean";
import { CellLink } from "./cell-link";
import { CellNumerical } from "./cell-numerical";
import { CellText } from "./cell-text";
import { CellListItem } from "./cell-list-item";
import { CellHeader } from "./cell-header";
import { CellDate } from "./cell-date";
import { CellActionLink } from "./cell-action-link";

/**Properties that apply to each cell variant*/
export interface CoreCellProps extends HTMLAttributes<HTMLDivElement> {
  /**The content inside of the cell to display when nothing is provided*/
  defaultCellValue?: string;
  /**Classes to apply to the cell container when no children are applied*/
  emptyCellValueClass?: string;
  /**
   * The child to display when the table component is in a loading state
   */
  skeleton?: ReactNode;
  /**Whether the cell container is focusable. By default, this value is true. It's disabled
   * In Cell.Action and other scenarios that contain an input field.
   * This way, there isn't an additional element to tab through
   */
  focusable?: boolean;

  /**
   * A friendly name that is included in visually hidden elements to provide more context about a cell.
   *
   * This provides contextual information to users that may be using a screen reader.
   * @example
   * - Loading 'customer data'
   * - No 'order number' available
   * */
  name?: string;

  /**How the text of a cell should be truncated*/
  truncatePosition?: "start" | "center" | "end";

  /**Whether the text of a cell can wrap multiple lines before truncating*/
  enableTextWrapping?: boolean;

  /**The maximum number of lines text can wrap before truncating*/
  maxLines?: number;

  /**Whether to display a tooltip on truncated text when hovering over it
   * @default true
   */
  showTooltip?: boolean;
}

export type CellAlignment = "left" | "center" | "right";

export interface CellProps extends CoreCellProps {
  alignment?: CellAlignment;
}

export const Cell = ({
  children,
  alignment,
  defaultCellValue,
  emptyCellValueClass,
  skeleton,
  focusable = true,
  name = "data",
  truncatePosition = "end",
  enableTextWrapping = false,
  className,
  maxLines = 1,
  ...rest
}: PropsWithChildren<CellProps>) => {
  const alignmentStyle = () => {
    switch (alignment) {
      case "left":
      default:
        return styles.cellLeft;
      case "center":
        return styles.cellCenter;
      case "right":
        return styles.cellRight;
    }
  };

  const {
    defaultEmptyCellValue,
    loading,
    columnAlignment,
    setColumnAlignment,
    isMobile,
  } = useTable();

  const { id } = useColumn();

  useEffect(() => {
    if (isMobile) return;
    if (columnAlignment.some((x) => x.id === id)) return;
    setColumnAlignment &&
      setColumnAlignment((prev) => [
        ...prev.filter((x) => x.id !== id),
        {
          id: id,
          alignment: alignment ?? "left",
        },
      ]);
  }, [alignment]);

  const containerClassNames = classNames(styles.cell, alignmentStyle());
  const contentClassNames = classNames(
    styles.cellContent,
    styles.cellInnerPadding,
    emptyCellValueClass,
    {
      [styles.cellEmpty]: !children,
      [styles.ellipsisStart]: truncatePosition === "start",
      [styles.noWrap]: !enableTextWrapping,
      [styles.cellContentMultipleLines]: enableTextWrapping,
    },
    className,
  );

  return (
    <div
      role="cell"
      className={containerClassNames}
      tabIndex={focusable && children ? 0 : undefined}
    >
      <div
        style={{
          WebkitLineClamp: maxLines,
        }}
        {...rest}
        className={contentClassNames}
      >
        {loading && <VisuallyHidden>Loading {name}</VisuallyHidden>}
        {!children && !loading && (
          <VisuallyHidden>No {name} available</VisuallyHidden>
        )}
        {loading
          ? skeleton ?? <Skeleton />
          : children != undefined && children != ""
          ? children
          : defaultCellValue ?? defaultEmptyCellValue}
      </div>
    </div>
  );
};

Cell.Header = CellHeader;
Cell.Action = CellAction;
Cell.Boolean = CellBoolean;
Cell.Link = CellLink;
Cell.Numerical = CellNumerical;
Cell.Text = CellText;
Cell.ListItem = CellListItem;
Cell.Date = CellDate;
Cell.ActionLink = CellActionLink;
